import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AnimatedSection } from '../components/AnimatedSection';
import { MagneticButton } from '../components/MagneticButton';
import { ArrowRight, ArrowUpRight, MessageCircle, Lightbulb, PenTool, Rocket } from 'lucide-react';

/* ── Data ───────────────────────────────────────────────────────── */
const services = [
  { num: '01', title: 'Brand Strategy & Positioning', desc: 'We clarify who you are, what you stand for, and why it matters — before a single pixel is designed.' },
  { num: '02', title: 'Packaging & Physical Design',  desc: 'Packaging that works as a silent salesperson — communicating your brand\'s value on shelf and in hand.' },
  { num: '03', title: 'Product UI/UX Design',         desc: 'Research-backed digital products built for real users, from zero-to-one through to shipped.' },
  { num: '04', title: 'Logo & Visual Identity',       desc: 'Identity systems that scale — mark, colour, typography, motion — consistent across every touchpoint.' },
  { num: '05', title: '2D Animation & Video',         desc: 'Motion design and promotional video production that brings brand stories to life.' },
  { num: '06', title: 'Instagram & Content Strategy', desc: 'Strategic Reels and content that grows your audience and converts — consistently.' },
];

const processSteps = [
  { num: '01', title: 'Understanding Requirements', desc: 'We begin by listening — understanding your brand, goals, audience, and what success looks like for your project.', icon: MessageCircle },
  { num: '02', title: 'Strategy & Concept',         desc: 'We define the creative direction and visual concept before any production begins.', icon: Lightbulb },
  { num: '03', title: 'Design & Execution',         desc: 'We bring the concept to life with precision — whether it\'s animation, packaging, or brand identity.', icon: PenTool },
  { num: '04', title: 'Final Delivery',             desc: 'Production-ready files delivered with revision rounds included. Everything you need to go live.', icon: Rocket },
];

const portfolio = [
  { title: 'Planet Soul',   tag: 'Packaging Design', year: '2024', img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/zce9xp77_Image%207.png' },
  { title: 'WayWheel',      tag: '2D Animation',     year: '2025', img: 'https://images.unsplash.com/photo-1705046332420-abc89468ef92?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800' },
  { title: 'SGN Marine',    tag: '2D Animation',     year: '2025', img: 'https://images.unsplash.com/photo-1767507006455-538274e7be76?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800' },
];

const testimonials = [
  { quote: 'Outbox Thinking perfectly captured our brand story through engaging 2D animation. Professional and timely delivery.', author: 'WayWheel', role: '2D Animation' },
  { quote: 'The animation video helped us clearly communicate our services. Great work and smooth process.', author: 'Guru', role: 'SGN Marine' },
  { quote: 'Creative execution and clear communication throughout the project.', author: 'Charanpreet', role: 'Client' },
  { quote: 'The packaging design elevated our brand and gave it a premium presence.', author: 'Nitesh', role: 'Planet Soul' },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09 } } };
const fadeUp   = { hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25,0.1,0.25,1] } } };

/* ── Hero ───────────────────────────────────────────────────────── */
const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const imgY  = useTransform(scrollY, [0, 600], [0, 90]);
  const textY = useTransform(scrollY, [0, 600], [0, -50]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', background: 'var(--canvas)', borderBottom: '1px solid var(--border)', overflow: 'hidden', position: 'relative' }}
    >
      {/* Subtle grid background */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        opacity: 0.5,
      }} />

      {/* Parallax image - monochrome */}
      <motion.div style={{ y: imgY, position: 'absolute', inset: 0, zIndex: 1 }}>
        <img
          src="https://images.unsplash.com/photo-1750598243589-1cc3770356b8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.04, filter: 'grayscale(100%)' }}
        />
      </motion.div>

      <div className="container-site" style={{ position: 'relative', zIndex: 10, paddingTop: 120, paddingBottom: 80, width: '100%' }}>
        <motion.div style={{ y: textY }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="label-sm">Brand Strategy & Design Studio</span>
          </motion.div>

          <motion.h1
            className="heading-xl"
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.25,0.1,0.25,1] }}
            style={{ fontSize: 'clamp(56px, 9.5vw, 128px)', marginTop: 20, marginBottom: 28, maxWidth: '85vw' }}
          >
            Clarity<br />
            Before<br />
            <span style={{ color: 'var(--accent)' }}>Growth.</span>
          </motion.h1>

          <motion.p
            className="body-base"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            style={{ maxWidth: 480, marginBottom: 40, fontSize: 16 }}
          >
            We help brands explain themselves clearly — across digital, physical, and business touchpoints. We don&rsquo;t start with visuals. We start with understanding.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}
          >
            <Link to="/contact" style={{ textDecoration: 'none' }} data-testid="hero-book-call-btn">
              <MagneticButton className="btn-primary" data-testid="hero-primary-btn">
                Book a Strategy Call
              </MagneticButton>
            </Link>
            <Link to="/case-studies" style={{ textDecoration: 'none' }} data-testid="hero-view-work-btn">
              <MagneticButton className="btn-secondary" data-testid="hero-secondary-btn">
                View Case Studies <ArrowRight size={13} />
              </MagneticButton>
            </Link>
          </motion.div>
        </motion.div>

        {/* Monochrome UI Mockup */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="hidden lg:block"
          style={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)', width: 360 }}
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            className="card-border"
            style={{ padding: 24, background: 'var(--canvas)' }}
          >
            {/* Monochrome UI mockup */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ink-950)' }} />
              <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
              <div style={{ width: 40, height: 6, borderRadius: 3, background: 'var(--ink-100)' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[1,2,3,4].map(i => (
                <div key={i} className="card-border" style={{ height: 64, padding: 10, background: i === 1 ? 'var(--ink-950)' : 'var(--surface)' }}>
                  <div style={{ width: '60%', height: 6, borderRadius: 2, background: i === 1 ? 'rgba(255,255,255,0.2)' : 'var(--ink-100)', marginBottom: 6 }} />
                  <div style={{ width: '40%', height: 4, borderRadius: 2, background: i === 1 ? 'rgba(255,255,255,0.1)' : 'var(--border)' }} />
                </div>
              ))}
            </div>
            <div style={{ height: 4, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '72%', background: 'var(--ink-950)', borderRadius: 999 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
              <span className="font-mono" style={{ fontSize: 9, color: 'var(--ink-500)' }}>72% complete</span>
              <span className="font-mono" style={{ fontSize: 9, color: 'var(--accent)' }}>On track</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          style={{ position: 'absolute', bottom: -20, left: 40, display: 'flex', alignItems: 'center', gap: 12 }}
        >
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <div style={{ width: 1, height: 40, background: 'var(--ink-300)' }} />
          </motion.div>
          <span className="label-sm" style={{ color: 'var(--ink-300)', fontSize: 9 }}>Scroll</span>
        </motion.div>
      </div>
    </section>
  );
};

/* ── Services ───────────────────────────────────────────────────── */
const Services = () => (
  <section data-testid="services-section" style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)' }}>
    <div className="container-site" style={{ paddingTop: 96, paddingBottom: 96 }}>
      <AnimatedSection style={{ marginBottom: 56 }}>
        <span className="label-sm">What We Do</span>
        <h2 className="heading-lg" style={{ fontSize: 'clamp(36px, 5vw, 60px)', marginTop: 12 }}>Capabilities</h2>
      </AnimatedSection>

      <motion.div
        initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
        data-testid="services-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}
      >
        {services.map((s, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            data-testid={`service-card-${i}`}
            className="card-border"
            style={{ background: 'var(--canvas)', padding: '32px 28px', position: 'relative', cursor: 'default', borderRadius: 12 }}
            whileHover={{ background: 'var(--surface)', y: -4 }}
            transition={{ duration: 0.25 }}
          >
            <span className="font-mono" style={{ fontSize: 11, color: 'var(--accent)', display: 'block', marginBottom: 16, letterSpacing: '0.1em' }}>{s.num}</span>
            <h3 className="heading-md" style={{ fontSize: 18, marginBottom: 12, lineHeight: 1.3 }}>{s.title}</h3>
            <p className="body-base" style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-500)' }}>{s.desc}</p>
            <ArrowUpRight size={14} style={{ position: 'absolute', top: 28, right: 24, color: 'var(--ink-300)' }} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ── Process ────────────────────────────────────────────────────── */
const Process = () => {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  return (
    <section data-testid="process-section" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div className="container-site" style={{ paddingTop: 96, paddingBottom: 96 }}>
        <AnimatedSection style={{ marginBottom: 64 }}>
          <span className="label-sm">How We Work</span>
          <h2 className="heading-lg" style={{ fontSize: 'clamp(36px, 5vw, 60px)', marginTop: 12 }}>Our Process</h2>
        </AnimatedSection>

        <div ref={ref}>
          {/* Progress line - desktop only */}
          <div className="hidden md:block" style={{ height: 2, background: 'var(--border)', position: 'relative', marginBottom: 48 }}>
            <motion.div 
              style={{ position: 'absolute', inset: 0, background: 'var(--ink-950)', transformOrigin: 'left' }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: inView ? 1 : 0 }}
              transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
            />
          </div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={stagger}
            data-testid="process-steps"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 32 }}
          >
            {processSteps.map((step, i) => {
              const IconComponent = step.icon;
              return (
                <motion.div 
                  key={i} 
                  variants={fadeUp} 
                  style={{ position: 'relative', padding: 24, background: 'var(--canvas)', borderRadius: 12, border: '1px solid var(--border)' }}
                  whileHover={{ y: -4, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Icon */}
                  <div style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: 12, 
                    background: 'var(--ink-950)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    marginBottom: 20
                  }}>
                    <IconComponent size={22} style={{ color: 'var(--canvas)' }} />
                  </div>
                  
                  {/* Step number badge */}
                  <span className="font-mono" style={{ 
                    position: 'absolute',
                    top: 24,
                    right: 24,
                    fontSize: 11, 
                    color: 'var(--accent)', 
                    letterSpacing: '0.08em',
                    background: 'var(--surface)',
                    padding: '4px 10px',
                    borderRadius: 20
                  }}>
                    STEP {step.num}
                  </span>
                  
                  <h3 className="heading-md" style={{ fontSize: 18, marginBottom: 10 }}>{step.title}</h3>
                  <p className="body-base" style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-500)' }}>{step.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ── Portfolio preview ──────────────────────────────────────────── */
const PortfolioPreview = () => (
  <section data-testid="portfolio-section" style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)' }}>
    <div className="container-site" style={{ paddingTop: 96, paddingBottom: 96 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, flexWrap: 'wrap', gap: 16 }}>
        <AnimatedSection>
          <span className="label-sm">Selected Work</span>
          <h2 className="heading-lg" style={{ fontSize: 'clamp(36px, 5vw, 60px)', marginTop: 12 }}>Case Studies</h2>
        </AnimatedSection>
        <AnimatedSection delay={0.15}>
          <Link to="/case-studies" data-testid="view-all-work-link" style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: 'var(--accent)', fontFamily: 'Inter', fontSize: 13, fontWeight: 500 }}>
            View All Work <ArrowRight size={13} />
          </Link>
        </AnimatedSection>
      </div>

      <motion.div
        initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}
      >
        {portfolio.map((item, i) => (
          <motion.div key={i} variants={fadeUp}>
            <Link to="/case-studies" data-testid={`portfolio-card-${i}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
              <div className="port-card card-border" style={{ overflow: 'hidden', position: 'relative', borderRadius: 8 }}>
                <div style={{ position: 'relative', paddingTop: '62%', overflow: 'hidden' }}>
                  <motion.img
                    src={item.img} alt={item.title}
                    className="img-mono"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    whileHover={{ scale: 1.04 }}
                    transition={{ duration: 0.5, ease: [0.25,0.1,0.25,1] }}
                  />
                  <div className="port-overlay" />
                </div>
                <div style={{ padding: '20px 24px 24px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span className="tag">{item.tag}</span>
                    <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-300)' }}>{item.year}</span>
                  </div>
                  <h3 className="heading-md" style={{ fontSize: 17 }}>{item.title}</h3>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ── Testimonials ───────────────────────────────────────────────── */
const Testimonials = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="testimonials-section" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div className="container-site" style={{ paddingTop: 96, paddingBottom: 96 }}>
        <AnimatedSection style={{ marginBottom: 48 }}>
          <span className="label-sm">Client Stories</span>
        </AnimatedSection>

        <div style={{ position: 'relative', minHeight: 240 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: [0.25,0.1,0.25,1] }}
              className="testimonial-wrap"
              data-testid={`testimonial-${active}`}
            >
              <p className="heading-md" style={{ fontSize: 'clamp(18px, 2.5vw, 26px)', fontWeight: 500, lineHeight: 1.45, marginBottom: 32, color: 'var(--ink-950)', letterSpacing: '-0.02em' }}>
                &ldquo;{testimonials[active].quote}&rdquo;
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ink-950)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: 'var(--canvas)', fontSize: 12, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
                    {testimonials[active].author.charAt(0)}
                  </span>
                </div>
                <div>
                  <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: 'var(--ink-950)' }}>{testimonials[active].author}</p>
                  <p className="label-sm" style={{ marginBottom: 0, color: 'var(--ink-500)' }}>{testimonials[active].role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`testimonial-dot-${i}`} style={{
                height: 2, width: i === active ? 28 : 8, background: i === active ? 'var(--ink-950)' : 'var(--ink-300)',
                border: 'none', cursor: 'pointer', padding: 0, borderRadius: 1,
                transition: 'width 0.3s ease, background 0.3s ease',
              }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Download Deck Section ──────────────────────────────────────── */
const DownloadDeck = () => (
  <section data-testid="download-deck-section" style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)' }}>
    <div className="container-site" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <AnimatedSection style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <span className="label-sm" style={{ justifyContent: 'center' }}>Resources</span>
        <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginTop: 16, marginBottom: 16 }}>
          Download Our Deck
        </h2>
        <p className="body-base" style={{ marginBottom: 32 }}>
          Learn more about who we are and what we offer. Download our company profile or services deck.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            disabled
            data-testid="download-company-profile-btn"
            className="btn-secondary"
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            Company Profile
          </button>
          <button
            disabled
            data-testid="download-services-deck-btn"
            className="btn-secondary"
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            Services Deck
          </button>
        </div>
        <p className="font-mono" style={{ fontSize: 10, color: 'var(--ink-300)', marginTop: 16, letterSpacing: '0.06em' }}>
          COMING SOON
        </p>
      </AnimatedSection>
    </div>
  </section>
);

/* ── Final CTA ──────────────────────────────────────────────────── */
const FinalCTA = () => (
  <section data-testid="final-cta-section" style={{ background: 'var(--ink-950)' }}>
    <div className="container-site" style={{ paddingTop: 120, paddingBottom: 120 }}>
      <AnimatedSection>
        <span className="label-sm" style={{ color: 'var(--ink-500)' }}>Ready to Build Clarity?</span>
        <h2
          className="heading-xl"
          style={{ fontSize: 'clamp(40px, 7vw, 96px)', color: 'var(--canvas)', margin: '20px 0 12px' }}
        >
          If this made sense,
        </h2>
        <p className="heading-md" style={{ fontSize: 'clamp(20px, 3vw, 40px)', color: 'var(--accent-light)', marginBottom: 40, fontWeight: 400, letterSpacing: '-0.02em' }}>
          we should probably talk.
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Link to="/contact" data-testid="cta-start-project-btn" style={{ textDecoration: 'none' }}>
            <MagneticButton
              data-testid="cta-btn"
              style={{ background: 'var(--canvas)', color: 'var(--ink-950)', border: 'none', padding: '14px 32px', borderRadius: 6, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Start Your Project <ArrowRight size={14} />
            </MagneticButton>
          </Link>
          <Link to="/contact" data-testid="cta-consultation-btn" style={{ textDecoration: 'none' }}>
            <MagneticButton
              data-testid="cta-secondary-btn"
              style={{ background: 'transparent', color: 'var(--canvas)', border: '1px solid var(--ink-500)', padding: '14px 32px', borderRadius: 6, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Get a Free Consultation
            </MagneticButton>
          </Link>
        </div>
      </AnimatedSection>
    </div>
  </section>
);

/* ── Page ───────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Services />
      <Process />
      <PortfolioPreview />
      <Testimonials />
      <DownloadDeck />
      <FinalCTA />
    </div>
  );
}
